import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MiQuoteService } from '../../services/mi-quote.service';
import { MiDetails, MiQuote, MiQuoteResponse, miSelectedVendors } from '../../../disclosure/models/mi-quote.model';
import { Observable, Subject, debounceTime, map } from 'rxjs';
import { FormGroup, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { SpinnerService } from '../../services/spinner.service';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { PmiType } from '../../../cmg-1003/models';
import { miQuoteMappings } from '../../../cmg-1003/models/lookups/mi-quote.lookups';
import { truncateEllipses } from '../../utils';
import { Cmg1003ConstantsService } from '../../../cmg-1003/services/cmg-1003-constants.service';
import { MiQuoteComponent } from '../mi-quote/miquote.component';
import { TableModule } from 'primeng/table';
import { AsyncPipe } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { CmgBusyIndicatorComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-busy-indicator/cmg-busy-indicator.component';

@Component({
  selector: 'cmg-clear2-mi-quote-result',
  templateUrl: './miquoteresult.component.html',
  styleUrls: ['./miquoteresult.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TableModule, PrimeTemplate, ButtonDirective, CmgBusyIndicatorComponent, AsyncPipe],
})
export class MiQuoteResultComponent implements OnInit, OnDestroy {
  config = inject(DynamicDialogConfig);
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _miQuoteService = inject(MiQuoteService);
  private readonly _dialogService = inject(DialogService);
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _cmg1003Service = inject(Cmg1003Service);
  private readonly _messageService = inject(MessageService);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);

  companies: MiQuoteResponse = {} as MiQuoteResponse;
  dialogRef: DynamicDialogRef;
  miQuoteDetails: MiDetails;
  fileDataId$: Observable<number>;
  upfrontMiFactor$: number;
  miMonthlyPrem$: number;
  miQuestResult: MiQuote = {} as MiQuote;
  coverage: number;
  miPFFFinancingCalcOption$: number;
  fileDataId: number;
  upFrontMiFactor: number;
  miQuoteResultForm: FormGroup;
  selectedCompanies: miSelectedVendors[];
  selectedCompany: miSelectedVendors;
  pmiType: number;
  emptyResult = false;
  errorDisplayMaxLength = 60;
  miQuoteData: MiQuote;
  truncateEllipses = truncateEllipses;

  isLoading$ = this._spinnerService.count$.pipe(
    debounceTime(1),
    map((t) => t > 0),
  );

  private readonly _destroyed$ = new Subject<boolean>();

  ngOnInit(): void {
    this.companies = this.config.data?.companies;
    this.selectedCompanies = this.config.data?.companies.miSelectedVendors;
    this.fileDataId = this.config.data?.companies.fileDataId;
    this.upFrontMiFactor = this.config.data?.companies.miMethod;
    this.pmiType = this.config.data?.pmiType;
    this.miQuoteData = this.config?.data?.miQuoteData;

    if (this.selectedCompanies.every((e) => e.errors?.length > 0)) {
      this.emptyResult = true;
    }

    /*
    Temp code until Byte mapping issues fixed
    create array of mi quote mapping objects with company name and number
     */
    const companyMappings = Object.entries(miQuoteMappings);

    /* do a for each of each company looking for matching mapping items and replace company number
    with corrected mapping of company number
    */
    this.selectedCompanies?.forEach((company) => {
      companyMappings.forEach((translate) => {
        if (company.miVendorName.toUpperCase().split(' ').join('') === translate[0].toUpperCase()) {
          company.miVendor = Number(translate[1]);
        }
      });
    });

    const firstValidQuoteCompany = this.selectedCompanies.find((c) => !c.errors || !(c.errors?.length > 0));
    if (firstValidQuoteCompany) {
      this.selectedCompany = firstValidQuoteCompany;
    }

    this.createForm();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  createForm() {
    this.miQuoteResultForm = this._formBuilder.group({
      selectedCompany: [null],
    });

    this.miQuoteResultForm.valueChanges.subscribe((e) => {
      this.miQuoteResultForm.setValue(e, { emitEvent: false });
    });
  }

  cancel() {
    this._openMIQuote(MiQuoteComponent);
  }

  save() {
    let premiumType = '';

    switch (this.pmiType) {
      case PmiType.monthly:
        premiumType = 'MonthlyNonRefundable';
        break;
      case PmiType.singlePremRefund:
        premiumType = 'SinglePremiumRefundable';
        break;
      case PmiType.singlePremNonRefund:
        premiumType = 'SinglePremiumNonRefundable';
        break;
      case PmiType.splitPremNonRefund:
        premiumType = 'SplitPremiumNonRefundable';
        break;
    }
    this.miQuoteDetails = {
      fileDataId: this.fileDataId,
      upfrontMiFactor: this.selectedCompany.mipffPerc,
      miMonthlyAmount: this.selectedCompany.miMoPremPerc,
      miCompanyName: this.selectedCompany.miVendor,
      miPremiumRatePrimary: this.selectedCompany.miMoPremPerc,
      miPremiumRateSecondary: this.selectedCompany.miMoPremPerc2,
      premiumType: premiumType,
      coveragePercent: this.selectedCompany.miCoveragePercent,
    };

    if ([PmiType.singlePremRefund, PmiType.singlePremNonRefund].includes(this.pmiType)) {
      this.miQuoteDetails.miPremiumRatePrimary = this.miQuoteDetails.upfrontMiFactor;
    }

    this._spinnerService.show();

    this._miQuoteService.updateMiDetails(this.miQuoteDetails).subscribe({
      next: () => {
        this._messageService.add({
          severity: 'success',
          summary: '',
          detail: 'MI Quote has been recorded successfully',
          key: 'mi-quote',
        });
        this._cmg1003Service.reset();
        this._dialogService.dialogComponentRefMap.forEach((dialog) => {
          dialog.destroy();
        });
      },
      error: () => {
        this._messageService.add({
          severity: 'error',
          summary: '',
          detail: 'Failed to Update Mi Quote',
          key: 'mi-quote',
          life: 5000,
        });
      },
      complete: () => {
        this._spinnerService.hide();
      },
    });
  }

  private _openMIQuote(component: any): DynamicDialogRef {
    this._ref.close();
    return this._dialogService.open(component, {
      data: {
        miQuoteData: this.config?.data?.miQuoteData,
      },
      width: '550px',
      styleClass: 'mi-quote-modal',
      contentStyle: { 'max-height': '600px', overflow: 'visible' },
      baseZIndex: this._cmg1003ConstantsService.modalZIndex,
    });
  }
}
